/**
 * Everything stays on the machine that runs the editor: files are never
 * uploaded anywhere. The working project is kept in localStorage and the
 * (potentially large) map image in IndexedDB.
 */
const PROJECT_KEY = 'dayz-editor:project:v1';
const SETTINGS_KEY = 'dayz-editor:settings:v1';

export type StoredProject = {
  files: { fileName: string; content: string; visible: boolean; color: string; locked: boolean }[];
  mapId: string;
  mapSize: number;
  savedAt: number;
};

export type StoredSettings = {
  showGrid: boolean;
  showLabels: boolean;
  snap: number;
  brushRadius: number;
  brushSpacing: number;
  defaultZoneRadius: number;
  /** 'official' = Bohemia's bundled map, a tile source id, 'custom' = your own image, 'none'. */
  basemap: string;
  izurviveVersion: string;
  showTiers: boolean;
  showBuildings: boolean;
};

/** Returns false when the browser refused (quota exceeded or storage disabled). */
export function saveProject(project: StoredProject): boolean {
  try {
    localStorage.setItem(PROJECT_KEY, JSON.stringify(project));
    return true;
  } catch {
    return false;
  }
}

export function loadProject(): StoredProject | null {
  try {
    const raw = localStorage.getItem(PROJECT_KEY);
    return raw ? (JSON.parse(raw) as StoredProject) : null;
  } catch {
    return null;
  }
}

export function clearProject(): void {
  try {
    localStorage.removeItem(PROJECT_KEY);
  } catch {
    /* ignore */
  }
}

export function saveSettings(settings: StoredSettings): void {
  try {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  } catch {
    /* ignore */
  }
}

export function loadSettings(): Partial<StoredSettings> | null {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    return raw ? (JSON.parse(raw) as StoredSettings) : null;
  } catch {
    return null;
  }
}

/* ---------------- map image (IndexedDB) ---------------- */

const DB_NAME = 'dayz-editor';
const STORE = 'images';

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveMapImage(mapId: string, blob: Blob): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).put(blob, mapId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}

export async function loadMapImage(mapId: string): Promise<Blob | null> {
  const db = await openDb();
  const blob = await new Promise<Blob | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readonly');
    const request = tx.objectStore(STORE).get(mapId);
    request.onsuccess = () => resolve((request.result as Blob) ?? null);
    request.onerror = () => reject(request.error);
  });
  db.close();
  return blob;
}

export async function deleteMapImage(mapId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    tx.objectStore(STORE).delete(mapId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
  db.close();
}
